import { Food, FoodStatus } from '@cf-shared/data-model/food';
import { isRecipe } from '@cf-shared/data-model/recipe';

/**
 * What happened when the food editor asked the server to take a food out of the
 * global, searchable food database on delete (df-274672bb1f5d).
 *
 * The three answers exist because only ONE of them is a problem, and the other two
 * are the ordinary case. Reporting all of them would bury the real one.
 */
export enum UnpublishOutcome {
  /** the server removed it, which is what we asked for */
  Removed = 'removed',
  /**
   * the request failed, but this user's own publish never put this food in the global
   * database — it was never sent, or the copy being deleted is a cache of somebody
   * else's food. Either way there was nothing here for us to remove, so the end state we
   * wanted is the end state we have.
   */
  NothingToRemove = 'nothing-to-remove',
  /**
   * the request failed for a food we believe IS in the global database. The
   * user's own copy is gone, so nothing will ever try again: the food stays
   * published and searchable for everybody else, and this is the condition worth
   * reporting.
   */
  StillPublished = 'still-published',
}

/**
 * Whether THIS USER's own publish put this food in the global food database, judged from
 * the client's own state rather than from what the server said.
 *
 * That is the question that decides whether a failed unpublish matters. The server
 * answers `Can't unpublish unpublished food` for any id the global database has never
 * seen, `Can't unpublish official food` for our own official content, and
 * `Can't unpublish unowned food` for somebody else's (`server/functions/src/publish-food.ts`).
 * Matching those sentences would break the day somebody rewords one, so the client works
 * it out from facts it already holds. Each check below rules out one way the answer is no:
 *
 * - An official food is the app's own content that this user merely has a cached copy of.
 *   Deleting the copy was never meant to touch the global one, and the server refuses it
 *   outright.
 * - `local.hashSource` is the author stamp `generateUserFoodId` writes onto every food this
 *   user creates or duplicates. `tryPublishUnpublishedFoodsAsync` deletes it only AFTER a
 *   successful publish, so a food still carrying one has not been published.
 * - An unshared recipe is never published at all — the same publish sweep filters those out
 *   before it sends anything.
 * - A food the app records as somebody ELSE's is again only a cached copy. This user's foods
 *   are stored as `FoodStatus.Private` — set when one is created, when one is duplicated, and
 *   again on the copy the server hands back after a successful publish — while a food pulled
 *   from the global database is stored as what `getFoodStatus` derived from its id.
 *
 * That last check deliberately treats a food carrying no status, or `FoodStatus.Unknown`,
 * as this user's own. Neither value says anything about who wrote the food, so the answer is
 * a guess either way, and reporting a failure that turns out not to matter costs far less
 * than staying silent about a food left published for ever.
 *
 * **What that last check gets wrong, and why it is still worth having.** `status` records
 * where the app read a doc from, not who wrote it. `getFoodByIdAsync` sets it from the id
 * whenever it reads a food out of the global database, and `getFoodStatus` maps every
 * `food/+UDF…` and `food/+UDR…` id to `Contributed` — this user's own published foods included. So an own food
 * that reached this device through the global database rather than through its own creation
 * reads as somebody else's, and a failed unpublish on it stays silent. There is no way to do
 * better on the client: the only authoritative answer is the `authorHash` the server checks,
 * which is an HMAC keyed by a secret this device does not have. The check is kept because the
 * alternative is worse — without it, every delete of a cached community food files a report,
 * and a report that fires constantly is how the one that matters gets missed.
 *
 * The stamp can be stale in one direction: a publish that succeeded but whose follow-up save
 * did not leaves the stamp on a published food. That is why this answer is used only to
 * CLASSIFY a failure and never to skip the unpublish request — skipping on a stale stamp
 * would leave a published food on the server for ever, which is the exact harm this whole
 * check exists for.
 */
export function wasFoodPublishedByThisUser(food: Food): boolean {
  // if it is one of our official foods, this user's copy is a cache and never ours to unpublish
  if (food._id?.startsWith('food/$')) {
    return false;
  }
  // if the author stamp is still on it, no successful publish has happened yet
  if (food.local?.hashSource) {
    return false;
  }
  // if it is a recipe the user never shared, the publish sweep skips it entirely
  if (isRecipe(food) && !food.local?.shared) {
    return false;
  }
  // if the app records the food as somebody else's, this user's copy is again only a cache.
  // A missing status, and the Unknown the global database stores, say nothing either way.
  if (food.status !== undefined && food.status !== FoodStatus.Unknown && food.status !== FoodStatus.Private) {
    return false;
  }
  return true;
}

/**
 * Which of the three outcomes a delete's unpublish request produced.
 *
 * `failed` covers both shapes of failure the call has: an error the server returned,
 * and an exception thrown after the retry layer gave up. Neither tells us the food
 * was removed, so both are judged the same way here.
 */
export function describeUnpublishOutcome(food: Food, failed: boolean): UnpublishOutcome {
  if (!failed) {
    return UnpublishOutcome.Removed;
  }
  return wasFoodPublishedByThisUser(food) ? UnpublishOutcome.StillPublished : UnpublishOutcome.NothingToRemove;
}
